export interface RewriteRequestToken {
  requestId: number;
  sourceRevision: number;
  scopeRevision: number;
  signal: AbortSignal;
}

export interface RewriteRequestGuard {
  updateSource(nextText: string): void;
  updateScope(nextScopeKey: string): void;
  begin(): RewriteRequestToken;
  canApply(token: RewriteRequestToken): boolean;
  isLatest(token: RewriteRequestToken): boolean;
  invalidate(): void;
}

/**
 * Tracks both the source revision and request order for asynchronous rewrites.
 * A value comparison alone is insufficient: a user may edit a field and then
 * restore its old value while the provider request is still in flight.
 */
export function createRewriteRequestGuard(
  initialText: string,
  initialScopeKey = '',
): RewriteRequestGuard {
  let currentText = initialText;
  let currentScopeKey = initialScopeKey;
  let sourceRevision = 0;
  let scopeRevision = 0;
  let latestRequestId = 0;
  let activeController: AbortController | null = null;

  const abortActiveRequest = () => {
    activeController?.abort();
    activeController = null;
  };

  return {
    updateSource(nextText: string) {
      if (nextText !== currentText) {
        abortActiveRequest();
        currentText = nextText;
        sourceRevision += 1;
      }
    },
    updateScope(nextScopeKey: string) {
      if (nextScopeKey !== currentScopeKey) {
        abortActiveRequest();
        currentScopeKey = nextScopeKey;
        scopeRevision += 1;
      }
    },
    begin() {
      abortActiveRequest();
      latestRequestId += 1;
      activeController = new AbortController();
      return {
        requestId: latestRequestId,
        sourceRevision,
        scopeRevision,
        signal: activeController.signal,
      };
    },
    canApply(token: RewriteRequestToken) {
      return !token.signal.aborted
        && token.requestId === latestRequestId
        && token.sourceRevision === sourceRevision
        && token.scopeRevision === scopeRevision;
    },
    isLatest(token: RewriteRequestToken) {
      return token.requestId === latestRequestId;
    },
    invalidate() {
      abortActiveRequest();
      latestRequestId += 1;
    },
  };
}

export function applyRewriteIfCurrent(
  guard: RewriteRequestGuard,
  token: RewriteRequestToken,
  apply: () => void,
): boolean {
  if (!guard.canApply(token)) return false;
  apply();
  return true;
}

export async function runGuardedRewriteRequest<T>(
  guard: RewriteRequestGuard,
  request: (signal: AbortSignal) => Promise<T>,
  apply: (result: T) => void,
  options: { onLatestSettled?: () => void } = {},
): Promise<boolean> {
  const token = guard.begin();
  try {
    const result = await request(token.signal);
    return applyRewriteIfCurrent(guard, token, () => apply(result));
  } catch (error) {
    if (!guard.canApply(token)) return false;
    throw error;
  } finally {
    if (guard.isLatest(token)) {
      options.onLatestSettled?.();
    }
  }
}
